import { useAsyncData, useRoute } from '#imports'
import { ref, watch } from 'vue'

import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'
import {
  reviewsConfig,
  reviewsRequestEndpoints,
} from '~/scopes/reviews/reviews-display/constants'

import { transformReviewsResponse } from '../utils/transformReviewsResponse'

function useFetchReviewsWithAttachments() {
  const route = useRoute()
  const locale = useI18nLocale()
  const { productId } = useUrlParams()
  const $httpFetch = useHttpFetch()

  const minRate = 4.7
  const maxAmount = 20
  const withAttachments = true

  const uuid: string = route.params?.uuid ? route.params.uuid : productId
  const routeName = route.name?.toString() ?? ''
  const endpoint = reviewsRequestEndpoints?.[routeName]?.getReviews
  const headers = reviewsConfig?.[routeName]?.headers
  if (!endpoint) return { data: ref(), error: ref(), pending: ref(false) }

  return useAsyncData(
    getAsyncDataKey('reviews-with-attachments', { uuid, locale, routeName }),
    () => {
      return $httpFetch(endpoint, {
        headers,
        pathParams: { uuid },
        query: {
          order_by: '-relevance_alt',
          global_rate__gte: minRate,
          page_size: maxAmount,
          with_attachments: withAttachments,
          translation_locale: locale,
        },
      })
    },
    {
      server: false,
      transform: ({ results }) => transformReviewsResponse(results),
      lazy: true,
    },
  )
}

export function useReviewsWithAttachments() {
  const route = useRoute()
  const logger = useLogger()

  const { data, error, pending } = useFetchReviewsWithAttachments()

  watch(
    error,
    () => {
      if (error.value instanceof Error) {
        logger.error(`[REVIEWS][${route.name?.toString()}] With attachments`, {
          error: error.value,
          owners: ['bot-squad-content-front'],
        })
      }
    },
    { immediate: true },
  )

  return {
    reviewsWithAttachments: data,
    isPendingReviewsWithAttachments: pending,
  }
}
